import React, {
  useState,
  useRef,
  useLayoutEffect,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import { Link, useLocation } from 'react-router-dom';
import { voteDeal, saveDeal, unsaveDeal } from '../../services/dealService';
import { getAffiliateRedirectUrl } from '../../services/affiliateService';
import { AuthContext } from '../../store/AuthContext';
import { OptimizedImage } from '../common/OptimizedImage';
import { BrandLogo } from '../common/BrandLogo';
import HighlightedText from '../common/HighlightedText';
import { useDealData } from '../../store/DealDataContext';
import { DealProps } from '../../types/deal';
import { trackSearchResult } from '../../services/analyticsService';
import AnalyticsService from '../../services/analyticsService';
import { resolveImageUrl } from '../../utils/imageUtils';
import { generateDealUrl, generateDealShareUrl } from '../../utils/dealUrlHelper';
import './NewDealCard.css';
import './NewDealCard-legacy.css';

// TooltipWrapper temporarily disabled for audit
// const TooltipWrapper: React.FC<{ content: string; children: ReactNode }> = ({ children }) => <>{children}</>;

// SVG Icons (can be moved to separate components if preferred)
const BookmarkIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
  </svg>
);

const ShareIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="18" cy="5" r="3"></circle>
    <circle cx="6" cy="12" r="3"></circle>
    <circle cx="18" cy="19" r="3"></circle>
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
  </svg>
);

const ThumbsUpIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path>
  </svg>
);

const CommentIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
  </svg>
);

interface NewDealCardProps {
  deal: DealProps;
  priority?: boolean; // Added for LCP optimization
  isLCP?: boolean; // Explicit LCP marking for critical images
  searchQuery?: string;
  searchPosition?: number;
}

const NewDealCard: React.FC<NewDealCardProps> = ({
  deal,
  priority = false,
  isLCP = false,
  searchQuery,
  searchPosition,
}) => {
  const currentPriceRef = useRef<HTMLSpanElement>(null);
  const [hideOriginalPrice, setHideOriginalPrice] = useState(false);

  // Ensure original price is hidden if current price is truncated
  useLayoutEffect(() => {
    const checkTruncation = () => {
      const priceEl = currentPriceRef.current;
      if (!priceEl) return;
      // If scrollWidth > clientWidth, the content is truncated
      setHideOriginalPrice(priceEl.scrollWidth > priceEl.clientWidth);
    };
    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [deal.price]);
  const { isAuthenticated } = useContext(AuthContext);
  const { getSavedStatus, getVoteStatus, updateSavedStatus, updateVoteStatus } = useDealData();

  const [currentScore, setCurrentScore] = useState(deal?.dealScore || 0);
  const [isVoting, setIsVoting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [imageError] = useState(false);

  const dealId = useMemo(() => deal._id, [deal._id]);
  const isSaved = getSavedStatus(dealId) ?? false;
  const currentVoteStatus = getVoteStatus(dealId) ?? 0;

  useEffect(() => {
    setCurrentScore(deal?.dealScore || 0);
  }, [deal?.dealScore]);

  const getImageSource = () => {
    // Default to brand logo as fallback
    let imageSrc: string | { avif?: string; webp?: string; jpeg: string } =
      '/images/logos/deals-n-steals-logo-optimized.svg';
    let imageWidth = 400;
    let imageHeight = 400;

    if (deal.optimizedImages && deal.optimizedImages.length > 0) {
      const mainImageSet = deal.optimizedImages[0];

      // Use medium thumbnails for deal cards (400x400 square)
      if (mainImageSet.thumbnails?.medium) {
        imageSrc = {
          avif: mainImageSet.thumbnails.medium.avif
            ? resolveImageUrl(mainImageSet.thumbnails.medium.avif)
            : undefined,
          webp: mainImageSet.thumbnails.medium.webp
            ? resolveImageUrl(mainImageSet.thumbnails.medium.webp)
            : undefined,
          jpeg: resolveImageUrl(mainImageSet.thumbnails.medium.jpeg),
        };
      } else {
        // Fallback to original image if thumbnails not available
        imageSrc = {
          avif: mainImageSet.avif ? resolveImageUrl(mainImageSet.avif) : undefined,
          webp: mainImageSet.webp ? resolveImageUrl(mainImageSet.webp) : undefined,
          jpeg: resolveImageUrl(mainImageSet.jpeg),
        };
        if (mainImageSet.metadata?.dimensions) {
          imageWidth = mainImageSet.metadata.dimensions.width;
          imageHeight = mainImageSet.metadata.dimensions.height;
        }
      }
    } else if (deal.imageUrl) {
      imageSrc = resolveImageUrl(deal.imageUrl);
    }

    return {
      src: imageSrc,
      width: imageWidth,
      height: imageHeight,
      loading: priority || isLCP ? 'eager' : 'lazy',
      isLCP: isLCP,
    };
  };

  const imageData = getImageSource();

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      // Basic time ago logic, replace with a library for more accuracy
      const date = new Date(dateString);
      const now = new Date();
      const seconds = Math.round((now.getTime() - date.getTime()) / 1000);
      const minutes = Math.round(seconds / 60);
      const hours = Math.round(minutes / 60);
      const days = Math.round(hours / 24);

      if (seconds < 60) return `Just now`;
      if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
      if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  const formatPrice = (price: number | undefined) => {
    if (typeof price !== 'number') return '$N/A';
    try {
      return price.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      });
    } catch (error) {
      console.error('Error formatting price:', error);
      return '$0.00';
    }
  };

  const handleVoteInternal = async (voteValue: 1 | -1) => {
    if (!isAuthenticated) {
      alert('Please log in to vote');
      return;
    }
    if (!dealId || isVoting) return;
    setIsVoting(true);
    try {
      const updatedDealData = await voteDeal(dealId, voteValue);
      setCurrentScore(updatedDealData.dealScore);
      updateVoteStatus(dealId, updatedDealData.voteStatus);
    } catch (error) {
      console.error('Error voting on deal:', error);
      // Optionally revert optimistic updates or show error to user
    }
    setIsVoting(false);
  };

  const handleSaveInternal = async () => {
    if (!isAuthenticated) {
      alert('Please log in to save deals');
      return;
    }
    if (!dealId || isSaving) return;
    setIsSaving(true);
    try {
      if (isSaved) {
        await unsaveDeal(dealId);
        updateSavedStatus(dealId, false);
      } else {
        await saveDeal(dealId);
        updateSavedStatus(dealId, true);
      }
    } catch (error) {
      console.error('Error saving deal:', error);
    }
    setIsSaving(false);
  };

  const handleViewDealClick = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    // Prevent default link navigation - we'll handle it via JavaScript
    event.preventDefault();

    if (!dealId || !deal.dealURL) return;

    // Track both deal view and deal click for "View Deal" button
    try {
      await AnalyticsService.trackDealView(deal._id);
      await AnalyticsService.trackDealClick(deal._id, 4); // Position 4 for "View Deal" button
    } catch (error) {
      console.error('Error tracking deal interaction:', error);
    }

    // Primary action: open affiliate URL
    try {
      const affiliateUrl = getAffiliateRedirectUrl(deal._id);
      window.open(affiliateUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error getting affiliate redirect URL:', error);
      window.open(deal.dealURL, '_blank', 'noopener,noreferrer');
    }
  };

  const handleShare = () => {
    // Placeholder for share functionality
    if (navigator.share) {
      navigator
        .share({
          title: deal.title,
          text: `Check out this deal: ${deal.title}`,
          url: generateDealShareUrl(deal), // SEO-friendly URL with slug
        })
        .catch(console.error);
    } else {
      alert('Share functionality not available or coming soon!');
    }
  };

  const discountPercentage =
    deal.discountPercent ||
    (deal.retailPrice && deal.price
      ? Math.round(((deal.retailPrice - deal.price) / deal.retailPrice) * 100)
      : 0);

  const location = useLocation();

  const handleSearchResultClick = useCallback(() => {
    // Track deal view and click for search result interactions
    const trackInteraction = async () => {
      try {
        await AnalyticsService.trackDealView(deal._id);
        await AnalyticsService.trackDealClick(deal._id, searchPosition);
      } catch (error) {
        console.error('Error tracking deal interaction:', error);
      }
    };

    trackInteraction();

    // Only track search-specific analytics if we're on a search results page
    if (location.pathname === '/search' && searchQuery && searchPosition !== undefined) {
      trackSearchResult(searchQuery, deal._id, searchPosition);
    }
  }, [location, searchQuery, searchPosition, deal._id]);

  // Helper function to handle deal interaction tracking
  const handleDealInteraction = async (clickPosition: number) => {
    try {
      await AnalyticsService.trackDealView(deal._id);
      await AnalyticsService.trackDealClick(deal._id, clickPosition);
    } catch (error) {
      console.error('Error tracking deal interaction:', error);
    }
  };

  // Feature flag for mobile-first CSS
  const useMobileFirstStyles = process.env.REACT_APP_MOBILE_FIRST_DEAL_CARD === 'true';
  const cardClassName = useMobileFirstStyles ? 'deal-card' : 'deal-card-legacy';

  return (
    <div className={cardClassName}>
      <div className="deal-card__image-container">
        <Link
          to={generateDealUrl(deal)}
          aria-label={`View details for ${deal.title}`}
          tabIndex={0}
          style={{ display: 'block' }}
          onClick={handleSearchResultClick}
        >
          {!imageError ? (
            <OptimizedImage
              src={typeof imageData.src === 'object' ? imageData.src.jpeg : imageData.src}
              alt={deal.title || 'Deal image'}
              className="deal-card__image"
              width={imageData.width}
              height={imageData.height}
              loading={imageData.loading as 'eager' | 'lazy'}
              fetchpriority={imageData.isLCP ? 'high' : 'auto'}
            />
          ) : (
            <div className="deal-card__image-placeholder">
              <BrandLogo className="fallback-logo" />
              <span>No Image Available</span>
            </div>
          )}
        </Link>

        {/* Action buttons overlay */}
        <div className="deal-card__image-actions">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleVoteInternal(1);
            }}
            className={`deal-card__image-action deal-card__image-action--thumbs-up ${currentVoteStatus === 1 ? 'deal-card__image-action--voted' : ''}`}
            disabled={isVoting || currentVoteStatus === 1}
            aria-label="Upvote Deal"
          >
            <ThumbsUpIcon />
            <span style={{ marginLeft: '2px', fontSize: '0.7em' }}>{currentScore}</span>
          </button>
          <Link
            to={`${generateDealUrl(deal)}#comments`}
            className="deal-card__image-action deal-card__image-action--comment"
            aria-label="View Comments"
            onClick={(e) => {
              e.stopPropagation();
              handleDealInteraction(5);
              // Ensure smooth scroll to comments section
              setTimeout(() => {
                const commentsElement = document.getElementById('comments');
                if (commentsElement) {
                  commentsElement.scrollIntoView({ behavior: 'smooth' });
                }
              }, 100);
            }}
          >
            <CommentIcon />
            <span style={{ marginLeft: '2px', fontSize: '0.7em' }}>{deal.commentsCount ?? 0}</span>
          </Link>
        </div>

        {/* Add deal type badge if data available, e.g. deal.type */}
        {deal.niche === 'Limited Time Offer' && (
          <span className="deal-card__type-badge">⚡ Limited</span>
        )}
        {discountPercentage > 0 && (
          <span className="deal-card__discount-badge">{discountPercentage}% OFF</span>
        )}
        {deal.lifecycleStatus === 'archived' && (
          <span className="deal-card__archived-badge">Archived</span>
        )}
      </div>

      <div className="deal-card__content">
        <h3 className="deal-card__title">
          <Link to={generateDealUrl(deal)} onClick={() => handleDealInteraction(2)}>
            {searchQuery ? (
              <HighlightedText text={deal.title || 'Untitled Deal'} searchTerm={searchQuery} />
            ) : (
              deal.title || 'Untitled Deal'
            )}
          </Link>
        </h3>

        {/* Updated Store Information Section */}
        {deal.store && typeof deal.store === 'object' && deal.store.name && (
          <div className="deal-card__store-info">
            {deal.store.link ? (
              <a
                href={deal.store.link}
                target="_blank"
                rel="noopener noreferrer"
                className="deal-card__store-link"
                onClick={() => handleDealInteraction(3)}
              >
                {deal.store.logoUrl && (
                  <img
                    src={deal.store.logoUrl}
                    alt={`${deal.store.name} Logo`}
                    className="deal-card__store-logo"
                    width="32"
                    height="32"
                  />
                )}
                <span className="deal-card__store-name">Sold by {deal.store.name}</span>
              </a>
            ) : (
              <>
                {deal.store.logoUrl && (
                  <img
                    src={deal.store.logoUrl}
                    alt={`${deal.store.name} Logo`}
                    className="deal-card__store-logo"
                    width="32"
                    height="32"
                  />
                )}
                <span className="deal-card__store-name">Sold by {deal.store.name}</span>
              </>
            )}
          </div>
        )}
        <div className="deal-card__pricing">
          <span className="deal-card__current-price" ref={currentPriceRef}>
            {formatPrice(deal.price)}
          </span>
          {deal.retailPrice && deal.retailPrice > (deal.price || 0) && !hideOriginalPrice && (
            <span className="deal-card__original-price">{formatPrice(deal.retailPrice)}</span>
          )}
        </div>
        <div className="deal-card__shipping">
          {deal.freeShipping || deal.shippingPrice === 0 ? (
            <>
              <span className="shipping-icon" aria-label="Free Shipping" role="img">
                🚚
              </span>
              <span className="shipping-label">FREE Shipping</span>
            </>
          ) : deal.shippingPrice ? (
            <>
              <span className="shipping-icon" aria-label="Shipping Price" role="img">
                📦
              </span>
              <span className="shipping-label">+{formatPrice(deal.shippingPrice)} Shipping</span>
            </>
          ) : null}
        </div>

        <div className="deal-card__meta">
          <button
            onClick={handleSaveInternal}
            className={`deal-card__meta-item deal-card__meta-item--save ${isSaved ? 'deal-card__meta-item--saved' : ''}`}
            disabled={isSaving}
            aria-label={isSaved ? 'Unsave Deal' : 'Save Deal'}
          >
            <BookmarkIcon />
            <span>{isSaving ? 'Saving...' : isSaved ? 'Saved' : 'Save'}</span>
          </button>
          <button
            onClick={handleShare}
            className="deal-card__meta-item deal-card__meta-item--share"
            aria-label="Share Deal"
          >
            <ShareIcon />
            <span>Share</span>
          </button>
        </div>

        <div className="deal-card__actions">
          <a
            href={generateDealUrl(deal)}
            onClick={handleViewDealClick}
            target="_blank"
            rel="noopener noreferrer"
            className="deal-card__cta-button"
          >
            View Deal &rarr;
          </a>
        </div>
      </div>

      <div className="deal-card__footer">
        <div className="deal-card__footer-left">
          <div className="deal-card__user">
            {deal.attributedTo ? (
              <>Posted by {deal.attributedTo}</>
            ) : (
              <>
                Posted by{' '}
                <span className="deal-card__user">
                  {deal.submittedByUserID?.displayName ||
                    deal.submittedByUserID?.username ||
                    'Unknown User'}
                </span>
              </>
            )}
          </div>
          <div className="deal-card__timestamp">{formatDate(deal.createdAt)}</div>
        </div>
      </div>
    </div>
  );
};

export default NewDealCard;
